/**
 * Raw shapes returned by the six `analytics/v2/trends/*` endpoints.
 *
 * None of these are described in `docs/grocefy-api.swagger.json` — the actions
 * return `IActionResult`, so swagger records the request models and nothing
 * else. These types were read off the C# DTOs in
 * `Gopanza.Api.Services.Abstractions/Models/Analytics/*TrendsDto.cs`, which is
 * the only authority for them.
 *
 * Two conventions apply throughout and are undone in `dashboard.api.ts` rather
 * than leaking into the view model:
 *
 * - every `*Growth` is a **fraction** (0.15 meaning 15%), rounded server-side
 *   to two decimals, so the smallest change it can express is one percent;
 * - when the previous period had nothing to compare against the server sends
 *   `1` if there is current activity and `0` if there is not, instead of
 *   marking the comparison as impossible.
 */

/** `TrendsOrderType`. The redesign only ever asks for every channel. */
export const ALL_ORDERS = 0;

/** `CategoriesGrouping`, in the order the API declares it. */
export const GROUPING_CODE = {
  division: 0,
  productGroup: 1,
  department: 2,
  category: 3,
} as const;

export interface TrendsRequest {
  StartDate: string;
  EndDate: string;
  /** Encoded id. Left out entirely by the redesign in favour of `BusinessIds`. */
  BusinessId?: string;
  /** Encoded ids; empty or absent means every store. */
  BusinessIds?: string[];
  CompareLastYear: boolean;
  Type: typeof ALL_ORDERS;
}

/** One bucket of `trends/sales`. The server decides the bucket width. */
export interface SaleTrendComparisonDto {
  isFutureDate: boolean;
  /**
   * True for the hour/day/month bucketings, false for the fallback that splits
   * an awkward range into twelve. Only useful for choosing a label format —
   * the real bounds are on the four date fields.
   */
  isGrouped: boolean;
  currentStartDate: string;
  currentEndDate: string;
  currentLabel: string | null;
  currentOrders: number;
  currentGrossSalesCost: number;
  currentGrossSales: number;
  currentNetSales: number;
  currentAverageTicket: number;
  currentAmountCollected: number;
  currentMerchandise: number;
  currentGiftCardRedemptions: number;

  previousStartDate: string;
  previousEndDate: string;
  previousLabel: string | null;
  previousOrders: number;
  previousGrossSalesCost: number;
  previousGrossSales: number;
  previousNetSales: number;
  previousAverageTicket: number;
  previousAmountCollected: number;
  previousMerchandise: number;
  previousGiftCardRedemptions: number;

  /** Note the transposed name — the period-level field is `grossSalesCostGrowth`. */
  grossSalesGrowthCost: number;
  ordersGrowth: number;
  grossSalesGrowth: number;
  netSalesGrowth: number;
  averageTicketGrowth: number;
  amountCollectedGrowth: number;
  merchandiseGrowth: number;
  giftCardRedemptionsGrowth: number;
}

export interface SaleTrendsDto {
  currentLabel: string | null;
  previousLabel: string | null;

  currentOrders: number;
  currentGrossSalesCost: number;
  currentGrossSales: number;
  currentNetSales: number;
  currentAverageTicket: number;
  currentAmountCollected: number;
  currentMerchandise: number;
  /** Paid for with gift cards on the period's orders, tax included. */
  currentGiftCardRedemptions: number;

  previousOrders: number;
  previousGrossSalesCost: number;
  previousGrossSales: number;
  previousNetSales: number;
  previousAverageTicket: number;
  previousAmountCollected: number;
  previousMerchandise: number;
  previousGiftCardRedemptions: number;

  grossSalesCostGrowth: number;
  ordersGrowth: number;
  grossSalesGrowth: number;
  netSalesGrowth: number;
  averageTicketGrowth: number;
  amountCollectedGrowth: number;
  merchandiseGrowth: number;
  giftCardRedemptionsGrowth: number;

  /**
   * Pre-filtered server-side to buckets with orders in one period or the other,
   * so a quiet day is missing from the series rather than present as a zero.
   */
  comparisons: SaleTrendComparisonDto[];
}

/** `trends/tendertypes` and `trends/cards` answer with the same shape. */
export interface TenderTypeTrendComparisonDto {
  name: string | null;
  currentOrders: number;
  currentRefundTotal: number;
  currentSalesTotal: number;
  currentAverageTicket: number;
  currentAmountCollected: number;
  currentMerchandise: number;

  previousOrders: number;
  previousRefundTotal: number;
  previousSalesTotal: number;
  previousAverageTicket: number;
  previousAmountCollected: number;
  previousMerchandise: number;

  ordersGrowth: number;
  refundTotalGrowth: number;
  salesTotalGrowth: number;
  averageTicketGrowth: number;
  amountCollectedGrowth: number;
  merchandiseGrowth: number;
}

export interface TenderTypesTrendsDto {
  currentLabel: string | null;
  previousLabel: string | null;
  comparisons: TenderTypeTrendComparisonDto[];
}

export interface CategoriesTrendComparisonDto {
  /** Encoded section id for department/category; the raw name for the other two. */
  id: string | null;
  nameSpa: string | null;
  nameEng: string | null;

  currentOrders: number;
  /** Units, despite the name. `*SalesTotal` is the money. */
  currentSales: number;
  currentSalesTotal: number;
  currentProfit: number;
  currentCost: number;
  currentProfability: number;

  previousOrders: number;
  previousSales: number;
  previousSalesTotal: number;
  previousProfit: number;
  previousCost: number;
  previousProfability: number;

  ordersGrowth: number;
  salesGrowth: number;
  salesTotalGrowth: number;
  profitGrowth: number;
}

export interface CategoriesTrendsDto {
  currentLabel: string | null;
  previousLabel: string | null;
  comparisons: CategoriesTrendComparisonDto[];
}

export interface ItemTrendComparisonDto {
  itemId: string | null;
  upc: string | null;
  brand: string | null;
  name: string | null;
  description: string | null;
  imageUrl: string | null;

  currentOrders: number;
  /** Units. */
  currentSales: number;
  currentSalesTotal: number;
  currentProfit: number;
  currentProfability: number;

  previousOrders: number;
  previousSales: number;
  previousSalesTotal: number;
  previousProfit: number;
  previousProfability: number;

  ordersGrowth: number;
  salesGrowth: number;
  salesTotalGrowth: number;
  profitGrowth: number;
}

export interface ItemsTrendsDto {
  currentLabel: string | null;
  previousLabel: string | null;
  comparisons: ItemTrendComparisonDto[];
  /**
   * Distinct items matching the request. Equal to the returned count when the
   * caller asked for a top-N rather than a page.
   */
  total: number;
}

/**
 * `trends/items` only. `Count` takes the top N; `Size` (with a zero-based
 * `Page`) pages through everything that matches instead, which is what makes
 * `SearchTerm` useful — the search then runs over the whole catalogue rather
 * than over whichever rows the top-N happened to include.
 */
export interface ItemsTrendsRequest extends TrendsRequest {
  Count?: number;
  SearchTerm?: string;
  Page?: number;
  Size?: number;
}

/**
 * `InventoryMovementReason`. The loss endpoint only ever reports the nine it
 * counts as loss, but the enum is wider, so unknown values fall back to a
 * generic label rather than an empty cell.
 */
export const LOSS_REASON_KEY: Record<number, string> = {
  0: 'dashboard.reasonNone',
  1: 'dashboard.reasonDamage',
  2: 'dashboard.reasonLost',
  3: 'dashboard.reasonFound',
  4: 'dashboard.reasonReplenishment',
  5: 'dashboard.reasonSampling',
  6: 'dashboard.reasonGift',
  7: 'dashboard.reasonSpoiled',
  8: 'dashboard.reasonTransferToKitchen',
  9: 'dashboard.reasonTransferToOtherStore',
  10: 'dashboard.reasonTransferToDeli',
};

export interface LossTrendComparisonDto {
  reason: number;
  /** Units. Negated server-side, so a loss arrives positive. */
  currentTotal: number;
  previousTotal: number;
  growth: number;
}

export interface LossTrendsDto {
  currentLabel: string | null;
  previousLabel: string | null;
  comparisons: LossTrendComparisonDto[];
}
